import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, LogOut } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { navItems } from './navItems';

export const ClientSidebar = ({ sidebarOpen, setSidebarOpen, clientInfo, messageSummary, onLogout }) => {
  const location = useLocation();
  const isActive = (path) => location.pathname === path;

  return (
    <aside
      className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r flex flex-col transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
      data-testid="client-sidebar"
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b flex-shrink-0">
        <Link to="/client/dashboard" className="flex items-center gap-2">
          <img
            src="https://customer-assets.emergentagent.com/job_sweet-circles-3/artifacts/o32nbzaj_logo%20cropped.jpeg"
            alt="MAD Donuts"
            className="h-10 w-auto"
          />
          <span className="font-bold text-lg text-gray-900">MAD Donuts</span>
        </Link>
        <button className="lg:hidden p-1" onClick={() => setSidebarOpen(false)}>
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Client Info */}
      {clientInfo && (
        <div className="p-4 border-b bg-gray-50 flex-shrink-0">
          <p className="font-semibold text-gray-900 truncate" data-testid="client-company-name">
            {clientInfo.company_name}
          </p>
          <p className="text-xs text-gray-500">{clientInfo.account_number}</p>
        </div>
      )}

      {/* Navigation — scrollable region */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-1" data-testid="client-nav">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isMessages = item.path === '/client/messages';
          const showBadge = isMessages && messageSummary.unread_count > 0;

          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive(item.path)
                  ? 'bg-amber-100 text-amber-900'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
              onClick={() => setSidebarOpen(false)}
            >
              <div className="relative">
                <Icon className="h-5 w-5 flex-shrink-0" />
                {showBadge && messageSummary.has_urgent && (
                  <span className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full animate-pulse" />
                )}
              </div>
              <span className="flex-1">{item.label}</span>
              {showBadge && (
                <Badge className={`text-xs ${messageSummary.has_urgent ? 'bg-red-500' : 'bg-amber-500'} text-white`}>
                  {messageSummary.unread_count}
                </Badge>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom section pinned at bottom */}
      <div className="p-4 border-t bg-white flex-shrink-0">
        <button
          onClick={onLogout}
          data-testid="logout-button"
          aria-label="Logout"
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 w-full"
        >
          <LogOut className="h-4 w-4" />
          Logout
        </button>
      </div>
    </aside>
  );
};
